// Wrapper visual para el admin dentro de rutas protegidas.
// Header con nombre + rol + logout; sidebar con nav; <main id="main">.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminLogout, fetchPendingCharges } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useColorScheme } from '@/hooks/useColorScheme';
import VersionChip from '@/components/VersionChip';
import HelpButton from '@/help/HelpButton';
import { clearDocsPass, ensureDocsPass } from '@/help/docs';
import { canSee, SECTION_PATH, type Section } from '@/lib/access';

// El menu, en orden. La etiqueta y el id de ayuda viven aca; quien lo ve, en
// lib/access.ts.
// `to` pisa la ruta de la seccion: "Cobros pendientes" vive con los permisos
// de Usuarios (muestra clientes) pero tiene su propia pantalla.
const NAV_ITEMS: readonly { section: Section; label: string; help: string; to?: string }[] = [
  { section: 'dashboard', label: 'Dashboard', help: 'shell.nav.dashboard' },
  { section: 'users', label: 'Usuarios', help: 'shell.nav.users' },
  { section: 'users', label: 'Cobros pendientes', help: 'shell.nav.charges', to: '/cobros' },
  { section: 'partners', label: 'Partners', help: 'shell.nav.partners' },
  { section: 'catalog', label: 'Catálogo', help: 'shell.nav.catalog' },
  { section: 'reports', label: 'Reportes', help: 'shell.nav.reports' },
  { section: 'invoices', label: 'Facturas', help: 'shell.nav.invoices' },
  { section: 'jobs', label: 'Jobs & DLQ', help: 'shell.nav.jobs' },
  { section: 'audit', label: 'Audit log', help: 'shell.nav.audit' },
  { section: 'disposable', label: 'Blacklist email', help: 'shell.nav.disposable' },
  { section: 'team', label: 'Equipo', help: 'shell.nav.team' },
  { section: 'coupons', label: 'Cupones', help: 'shell.nav.coupons' },
];

function ThemeToggle(): JSX.Element {
  const { colorScheme, toggle } = useColorScheme();
  const isDark = colorScheme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      data-help="shell.theme"
      aria-label={`Cambiar a tema ${isDark ? 'claro' : 'oscuro'}`}
      aria-pressed={isDark}
      title={`Tema ${isDark ? 'oscuro' : 'claro'} — click para alternar`}
      className="
        inline-flex items-center justify-center
        min-h-[2.75rem] min-w-[2.75rem]
        rounded-lg text-ink-muted hover:text-ink hover:bg-surface-muted
        dark:hover:text-ink-inverted dark:hover:bg-surface-dark-muted
        focus-visible:outline-2 focus-visible:outline-brand-light
        transition
      "
    >
      {/* Sun en dark (para indicar que el click va a light), moon en light. */}
      {isDark ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function RoleBadge({ role, name }: { role: string; name: string }): JSX.Element {
  const color = role === 'admin' ? 'bg-brand/20 text-brand' : 'bg-status-info/20 text-status-info';
  return (
    <span title={role} className={`text-xs rounded-full px-2 py-0.5 font-medium ${color}`}>
      {name}
    </span>
  );
}

function UserMenu(): JSX.Element | null {
  const session = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  if (!session) return null;

  async function onLogout(): Promise<void> {
    setOpen(false);
    await adminLogout();
    // El pase del manual no lo revoca nadie desde el borde: se borra acá, que
    // es el unico momento en que sabemos que la persona se esta yendo.
    clearDocsPass();
    navigate('/login', { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-help="shell.user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 min-h-[2.75rem] px-2 py-1 rounded-lg text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted focus-visible:outline-2 focus-visible:outline-brand-light"
      >
        <span className="hidden sm:inline">{session.display_name}</span>
        <RoleBadge role={session.role} name={session.role_name ?? session.role} />
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 bg-surface dark:bg-surface-dark border border-surface-border dark:border-surface-dark-border rounded-lg shadow-lg overflow-hidden z-30"
        >
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            role="menuitem"
            className="block px-4 py-2 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
          >
            Mi cuenta
          </Link>
          <div className="border-t border-surface-border dark:border-surface-dark-border" />
          <button
            type="button"
            onClick={onLogout}
            role="menuitem"
            data-help="shell.logout"
            className="block w-full text-left px-4 py-2 text-sm text-status-error hover:bg-surface-muted dark:hover:bg-surface-dark-muted"
          >
            Cerrar sesión
          </button>
          <div data-help="shell.version">
            <VersionChip />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminShell({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();

  // El manual vive en /docs, fuera de React, y se abre en una pestaña nueva.
  // Para que ese salto no rebote al login, la cookie del pase tiene que estar
  // puesta ANTES del primer clic — no en el momento del clic.
  useEffect(() => {
    if (session) void ensureDocsPass();
  }, [session]);

  const navLinkCls = ({ isActive }: { isActive: boolean }): string =>
    `flex items-center min-h-[2.75rem] px-3 py-2 rounded-lg text-sm whitespace-nowrap transition ${
      isActive
        ? 'bg-brand text-white font-medium shadow-sm'
        : 'text-ink-muted hover:bg-surface-muted hover:text-ink dark:hover:bg-surface-dark-muted dark:hover:text-ink-inverted'
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3" data-help="shell.brand">
            <span className="font-semibold tracking-tight">AuthSys</span>
            <span className="text-ink-muted text-sm">Backoffice</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <HelpButton />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>
      <div className="flex-1 flex flex-col md:flex-row w-full">
        <nav
          data-help="shell.nav"
          aria-label="Navegación principal"
          className="
            shrink-0
            bg-surface dark:bg-surface-dark
            border-b md:border-b-0 md:border-r
            border-surface-border dark:border-surface-dark-border
            md:w-56
            px-3 py-2 md:px-4 md:py-6
            flex flex-row md:flex-col gap-1
            overflow-x-auto md:overflow-x-visible
          "
        >
          {/* Que se dibuja sale de lib/access.ts, no de condiciones sueltas:
              el menu y las rutas tienen que decir lo mismo. */}
          {NAV_ITEMS.filter((item) => canSee(session, item.section)).map((item) => (
            <NavLink
              key={item.to ?? item.section}
              to={item.to ?? SECTION_PATH[item.section]}
              // /users no puede quedar activo en /cobros ni al reves.
              end={item.to !== undefined}
              data-help={item.help}
              className={navLinkCls}
            >
              {item.label}
              {item.to === '/cobros' ? <PendingChargesBadge /> : null}
            </NavLink>
          ))}
        </nav>
        <main id="main" className="flex-1 min-w-0 p-4 md:p-6">
          <div className="max-w-7xl mx-auto w-full">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * Contador del menu. Es lo que hace "evidente" un cobro no realizado: no hace
 * falta entrar a buscarlo. Rojo si hay algo que el sistema no va a resolver
 * solo (procesador fallando o vencida sin intento). Comparte cache con la
 * pantalla de Cobros pendientes.
 */
function PendingChargesBadge(): JSX.Element | null {
  const q = useQuery({
    queryKey: ['admin-pending-charges'],
    queryFn: () => fetchPendingCharges(),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const t = q.data?.totals;
  if (!t) return null;
  const total = t.blocked + t.overdue + t.grace + t.expired;
  if (total === 0) return null;
  const urgent = t.blocked + t.overdue > 0;
  return (
    <span
      aria-label={`${total} cobros pendientes`}
      className={`ml-auto pl-2 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums ${
        urgent ? 'bg-status-error text-white' : 'bg-status-warning/25 text-status-warning'
      }`}
    >
      {total}
    </span>
  );
}
